import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FileText, Plus, Loader2, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { ModuleGate } from "@/components/PlanNotIncluded";

type PRItem = { id?: string; name: string; qty: number; unit: string; unit_price_minor: number; inventory_item_id?: string | null; received_qty?: number };
type PR = { id: string; code: string; status: string; vendor_id: string | null; branch_id: string | null; items: PRItem[]; total_minor: number; expense_id: string | null; created_at: string };
type Vendor = { id: string; name: string };
type InvItem = { id: string; name: string; sku: string | null; unit: string };
type Branch = { id: string; name: string };

const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
    draft: { bg: "var(--neutral-bg)", fg: "var(--neutral-fg)" },
    pending_approval: { bg: "var(--warning-bg)", fg: "var(--warning-fg)" },
    approved: { bg: "var(--info-bg)", fg: "var(--info-fg)" },
    ordered: { bg: "var(--info-bg)", fg: "var(--info-fg)" },
    received: { bg: "var(--success-bg)", fg: "var(--success-fg)" },
    rejected: { bg: "var(--error-bg)", fg: "var(--error-fg)" },
    closed: { bg: "var(--neutral-bg)", fg: "var(--neutral-fg)" },
};

export default function PurchaseRequests() {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    const canCreate = hasPerm("procurement.create");
    const [rows, setRows] = useState<PR[]>([]);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [items, setItems] = useState<InvItem[]>([]);
    const [branches, setBranches] = useState<Branch[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>("");
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState<{ vendor_id: string; branch_id: string; notes: string; items: PRItem[] }>({
        vendor_id: "", branch_id: "", notes: "", items: [{ name: "", qty: 1, unit: "ədəd", unit_price_minor: 0 }],
    });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true); setErr(null);
        try {
            const q = statusFilter ? `?status=${statusFilter}` : "";
            const [pr, vs, it, br] = await Promise.all([
                api.get(`/procurement/requests${q}`),
                api.get("/procurement/vendors"),
                api.get("/inventory/items").catch(() => ({ data: { items: [] } })),
                api.get("/branches").catch(() => ({ data: { items: [] } })),
            ]);
            setRows(pr.data.items || []);
            setVendors(vs.data.items || []);
            setItems(it.data.items || []);
            setBranches(br.data.items || []);
        } catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

    const addLine = () => setForm({ ...form, items: [...form.items, { name: "", qty: 1, unit: "ədəd", unit_price_minor: 0 }] });
    const removeLine = (i: number) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
    const setLine = (i: number, patch: Partial<PRItem>) => setForm({ ...form, items: form.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) });

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.items.length || form.items.some((it) => !it.name.trim() || it.qty <= 0)) {
            setErr(t("procurement.pr.err.items_required")); return;
        }
        setSaving(true); setErr(null);
        try {
            await api.post("/procurement/requests", {
                vendor_id: form.vendor_id || null,
                branch_id: form.branch_id || null,
                notes: form.notes.trim() || null,
                items: form.items.map((it) => ({
                    name: it.name.trim(), qty: it.qty, unit: it.unit || "ədəd",
                    unit_price_minor: Math.max(0, Math.round(it.unit_price_minor)),
                    inventory_item_id: it.inventory_item_id || null,
                })),
            });
            setCreating(false);
            setForm({ vendor_id: "", branch_id: "", notes: "", items: [{ name: "", qty: 1, unit: "ədəd", unit_price_minor: 0 }] });
            await load();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setSaving(false); }
    };

    return (
        <ModuleGate module="procurement">
            <div className="space-y-4" data-testid="proc-pr-page">
                <header className="flex items-center gap-3 flex-wrap">
                    <FileText size={22} style={{ color: "var(--primary)" }} />
                    <h1 className="text-xl font-semibold flex-1" style={{ color: "var(--text)" }}>{t("procurement.pr.title")}</h1>
                    <select data-testid="proc-pr-filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                        className="rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }}>
                        <option value="">{t("common.all")}</option>
                        {["draft", "pending_approval", "approved", "ordered", "received", "rejected", "closed"].map((s) => (
                            <option key={s} value={s}>{t(`procurement.pr.status.${s}`)}</option>
                        ))}
                    </select>
                    {canCreate && (
                        <button data-testid="proc-pr-new-btn" onClick={() => setCreating(true)}
                            className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium"
                            style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                            <Plus size={16} /> {t("procurement.pr.new")}
                        </button>
                    )}
                </header>
                {err && <div data-testid="proc-pr-err" className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
                {loading && <div className="flex justify-center p-6"><Loader2 className="animate-spin" size={22} /></div>}
                {!loading && !rows.length && <div className="surface-card p-6 rounded-xl text-sm" style={{ color: "var(--text-muted)", background: "var(--card)", border: "1px solid var(--border)" }} data-testid="proc-pr-empty">{t("procurement.pr.empty")}</div>}
                {!!rows.length && (
                    <div className="overflow-x-auto rounded-xl border" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                        <table className="w-full text-sm">
                            <thead>
                                <tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                    <th className="text-left px-3 py-2">{t("procurement.pr.code")}</th>
                                    <th className="text-left px-3 py-2">{t("procurement.pr.status")}</th>
                                    <th className="text-left px-3 py-2">{t("procurement.pr.items_count")}</th>
                                    <th className="text-right px-3 py-2">{t("procurement.pr.total")}</th>
                                    <th className="px-3 py-2"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => {
                                    const tone = STATUS_TONE[r.status] || STATUS_TONE.draft;
                                    return (
                                        <tr key={r.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`proc-pr-row-${r.id}`}>
                                            <td className="px-3 py-2 font-mono text-xs" style={{ color: "var(--text)" }}>{r.code}</td>
                                            <td className="px-3 py-2">
                                                <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs" style={{ background: tone.bg, color: tone.fg }} data-testid={`proc-pr-status-${r.id}`}>
                                                    <span aria-hidden="true">●</span>
                                                    <span>{t(`procurement.pr.status.${r.status}`)}</span>
                                                </span>
                                            </td>
                                            <td className="px-3 py-2" style={{ color: "var(--text-muted)" }}>{r.items.length}</td>
                                            <td className="px-3 py-2 text-right font-mono" style={{ color: "var(--text)" }}>{(r.total_minor / 100).toFixed(2)}</td>
                                            <td className="px-3 py-2 text-right">
                                                <Link to={`/procurement/requests/${r.id}`} className="text-xs font-medium hover:underline" style={{ color: "var(--primary)" }} data-testid={`proc-pr-open-${r.id}`}>{t("common.open")}</Link>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {creating && (
                    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 overflow-y-auto" style={{ background: "rgba(0,0,0,0.55)" }} onClick={() => setCreating(false)}>
                        <div className="w-full max-w-2xl rounded-xl border p-5 my-8" style={{ background: "var(--card)", borderColor: "var(--border)" }} onClick={(e) => e.stopPropagation()} data-testid="proc-pr-modal">
                            <h2 className="text-lg font-semibold mb-4" style={{ color: "var(--text)" }}>{t("procurement.pr.new")}</h2>
                            <form onSubmit={submit} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                        <span>{t("procurement.pr.vendor")}</span>
                                        <select data-testid="proc-pr-vendor" value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
                                            className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }}>
                                            <option value="">—</option>
                                            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                                        </select>
                                    </label>
                                    <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                        <span>{t("procurement.pr.branch")}</span>
                                        <select data-testid="proc-pr-branch" value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                                            className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }}>
                                            <option value="">—</option>
                                            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                        </select>
                                    </label>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium" style={{ color: "var(--text)" }}>{t("procurement.pr.items")}</span>
                                        <button type="button" onClick={addLine} data-testid="proc-pr-add-line" className="text-xs font-medium" style={{ color: "var(--primary)" }}>+ {t("procurement.pr.add_line")}</button>
                                    </div>
                                    {form.items.map((it, i) => (
                                        <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`proc-pr-line-${i}`}>
                                            <label className="col-span-4 text-xs" style={{ color: "var(--text-muted)" }}>
                                                <span>{t("procurement.pr.item_name")}</span>
                                                <input required data-testid={`proc-pr-line-name-${i}`} value={it.name} onChange={(e) => setLine(i, { name: e.target.value })}
                                                    className="mt-1 w-full rounded-md px-2 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }} />
                                            </label>
                                            <label className="col-span-3 text-xs" style={{ color: "var(--text-muted)" }}>
                                                <span>{t("procurement.pr.link_item")}</span>
                                                <select data-testid={`proc-pr-line-item-${i}`} value={it.inventory_item_id || ""} onChange={(e) => setLine(i, { inventory_item_id: e.target.value || null })}
                                                    className="mt-1 w-full rounded-md px-2 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }}>
                                                    <option value="">—</option>
                                                    {items.map((x) => <option key={x.id} value={x.id}>{x.sku ? `${x.sku} · ` : ""}{x.name}</option>)}
                                                </select>
                                            </label>
                                            <label className="col-span-2 text-xs" style={{ color: "var(--text-muted)" }}>
                                                <span>{t("procurement.pr.qty")}</span>
                                                <input required type="number" step="0.01" min={0.01} data-testid={`proc-pr-line-qty-${i}`} value={it.qty} onChange={(e) => setLine(i, { qty: parseFloat(e.target.value || "0") })}
                                                    className="mt-1 w-full rounded-md px-2 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }} />
                                            </label>
                                            <label className="col-span-2 text-xs" style={{ color: "var(--text-muted)" }}>
                                                <span>{t("procurement.pr.price_minor")}</span>
                                                <input type="number" min={0} data-testid={`proc-pr-line-price-${i}`} value={it.unit_price_minor} onChange={(e) => setLine(i, { unit_price_minor: parseInt(e.target.value || "0", 10) })}
                                                    className="mt-1 w-full rounded-md px-2 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)", minHeight: 40 }} />
                                            </label>
                                            <button type="button" aria-label="remove" onClick={() => removeLine(i)} data-testid={`proc-pr-line-remove-${i}`} className="col-span-1 rounded-md p-2" style={{ color: "var(--error-fg)", minHeight: 40 }}>
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <label className="block text-xs" style={{ color: "var(--text-muted)" }}>
                                    <span>{t("common.notes")}</span>
                                    <textarea data-testid="proc-pr-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                                        className="mt-1 w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)" }} />
                                </label>
                                <div className="flex justify-end gap-2 pt-2">
                                    <button type="button" onClick={() => setCreating(false)} data-testid="proc-pr-cancel" className="rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text-muted)", minHeight: 40 }}>{t("common.cancel")}</button>
                                    <button type="submit" disabled={saving} data-testid="proc-pr-save" className="rounded-md px-3 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                                        {saving ? <Loader2 className="animate-spin" size={14} /> : t("common.save")}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </ModuleGate>
    );
}
